import { runtimeConfig } from '../config';

/**
 * Fallback list of e-mail domains whose accounts cannot use the self-service
 * "Forgot password" flow. These are internal accounts; password resets must be
 * handled by internal support only.
 *
 * Used whenever the deploy does not provide an explicit list — see
 * {@link PASSWORD_RESET_BLOCKED_DOMAINS}.
 */
export const DEFAULT_PASSWORD_RESET_BLOCKED_DOMAINS = [
  '@quality24.com.br',
  '@app.com.br',
];

/**
 * Parses a comma-separated list of e-mail domains into normalized, lower-cased
 * entries with a leading `@` (so `app.com.br` never matches `@myapp.com.br`).
 * Falls back to {@link DEFAULT_PASSWORD_RESET_BLOCKED_DOMAINS} when the value is
 * missing or has no usable entry.
 */
export const parseBlockedDomains = (value: string | undefined): string[] => {
  const domains = (value ?? '')
    .split(',')
    .map((domain) => domain.trim().toLowerCase())
    .filter(Boolean)
    .map((domain) => (domain.startsWith('@') ? domain : `@${domain}`));

  return domains.length ? domains : DEFAULT_PASSWORD_RESET_BLOCKED_DOMAINS;
};

/**
 * The effective list of blocked domains, resolved once at load time from the
 * runtime config injected by the deploy (`runtime-config.js`), then from the
 * build-time env var, then from the defaults.
 *
 * Enforcement is frontend-only (no backend check) in two places:
 * - LoginForm: hides the "Esqueci a senha" link when the typed email matches a blocked domain
 * - ForgotPasswordForm: Yup validation blocks submit with an inline error if the user
 *   navigates directly to the forgot-password page via URL
 */
export const PASSWORD_RESET_BLOCKED_DOMAINS = parseBlockedDomains(
  runtimeConfig.PASSWORD_RESET_BLOCKED_DOMAINS ||
    process.env.REACT_APP_PASSWORD_RESET_BLOCKED_DOMAINS,
);

/** Returns whether the given email belongs to a domain blocked from self-service password reset. */
export const isPasswordResetBlocked = (email: string | undefined): boolean =>
  !!email &&
  PASSWORD_RESET_BLOCKED_DOMAINS.some((domain) =>
    email.toLowerCase().endsWith(domain),
  );
